// Library Methods
import { useTranslation } from "react-i18next";
import { QueryObserverResult, RefetchOptions } from "@tanstack/react-query";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CustomCheckbox from "src/components/shared/inputs/CustomCheckbox/CustomCheckbox";
import TableCard from "src/components/shared/tables/TableCard/TableCard";
import EyeCompletionText from "src/components/shared/text/EyeCompletionText/EyeCompletionText";
import LinkDeviceTableCard from "src/components/containers/shared/LinkDeviceTableCard/LinkDeviceTableCard";
import ExamCardButtons from "./ExamCardButtons/ExamCardButtons";

// Types
import { ExamType } from "src/services/exams/types";
import { PatientType } from "src/services/patients/types";

// Styles
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

// Hooks
import useExamCardHelpers from "src/hooks/exams/useExamCardHelpers";

// Utils
import { isMultiExamPackage } from "src/utils/exams/examReportsHelper";

type ExtendedExamType = ExamType & {
  examStatus: string;
  examEyeOrder: string;
  examAlgorithm: string;
  examSchedule: string;
};

type ExamCardType = {
  exam: ExtendedExamType;
  selectedPatient: PatientType;
  isSelectedForDeletion: boolean;
  refetchExams: (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<ExamType[], Error>>;
  handleCheckboxClick: (
    _: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    id: string
  ) => void;
};

const ExamCard = ({
  exam,
  selectedPatient,
  isSelectedForDeletion,
  handleCheckboxClick,
  refetchExams,
}: ExamCardType) => {
  const { t } = useTranslation();

  const isSelectedForViewing = false;

  const theme = useTheme();
  const { elevatedSurface } = getSurfaceTokens(theme);
  const smallerThanSm = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    deviceToUse,
    examToUse,
    examStatus,
    completedExamsText,
    examTooltipText,
  } = useExamCardHelpers({ exam });

  return (
    <Grid container item xs={12}>
      {/* Link device card */}
      <Grid item xs={1.5}>
        <LinkDeviceTableCard
          device={deviceToUse}
          exam={examToUse}
          examStatus={examStatus}
        />
      </Grid>

      <Grid item xs={10.5}>
        <TableCard
          key={examToUse.id}
          isSelectedForViewing={isSelectedForViewing}
          isSelectedForDeletion={isSelectedForDeletion}
          onClick={() => {}}
          hasHoverState={false}
          variant="dark"
          backgroundColorOverride={
            theme.palette.mode === "dark" ? elevatedSurface : undefined
          }
        >
          <Grid container display="flex" alignItems="center">
            <Grid container item xs={12} display="flex" alignItems="center">
              <Grid item xs={1}>
                <CustomCheckbox
                  id={`exam-card-checkbox-${examToUse.id}`}
                  checkedFill={
                    isSelectedForViewing || isSelectedForDeletion
                      ? "white"
                      : "#202338"
                  }
                  checkedStroke={
                    isSelectedForViewing || isSelectedForDeletion
                      ? "#202338"
                      : "white"
                  }
                  checked={isSelectedForDeletion}
                  onClick={(
                    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
                  ) => {
                    event.stopPropagation();
                    handleCheckboxClick(event, examToUse.id);
                  }}
                />
              </Grid>

              <Grid container item xs={10} display="flex" alignItems="center">
                <Grid item xs={12} sm={2.4}>
                  <Tooltip
                    title={
                      <div style={{ whiteSpace: "pre-line" }}>
                        {isMultiExamPackage(examToUse)
                          ? completedExamsText
                          : examStatus?.status}
                      </div>
                    }
                  >
                    <Typography
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      maxWidth="fit-content"
                      sx={{
                        fontSize: "14px",
                        paddingRight: "16px",
                      }}
                    >
                      {smallerThanSm && (
                        <span style={{ fontWeight: 600 }}>{t("Status")}: </span>
                      )}
                      {examStatus?.status}
                    </Typography>
                  </Tooltip>
                </Grid>

                <Grid item xs={12} sm={2.4}>
                  <Box
                    sx={{
                      paddingRight: "16px",
                      position: { xs: "relative", sm: "absolute" },
                      top: { xs: "0px", sm: "4px" },
                      width: "inherit",
                      maxWidth: "100%",
                      display: "block",
                    }}
                  >
                    {smallerThanSm ? (
                      <div
                        style={{
                          display: "flex",
                          gap: "4px",
                          fontSize: "14px",
                          fontWeight: 600,
                        }}
                      >
                        {t("Eye")}: <EyeCompletionText exam={examToUse} />
                      </div>
                    ) : (
                      <EyeCompletionText exam={examToUse} />
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12} sm={3}>
                  <Tooltip
                    title={
                      <div style={{ whiteSpace: "pre-line" }}>
                        {examTooltipText}
                      </div>
                    }
                  >
                    <Typography
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      maxWidth="fit-content"
                      sx={{
                        fontSize: "14px",
                        paddingRight: "16px",
                      }}
                    >
                      {smallerThanSm && (
                        <span style={{ fontWeight: 600 }}>{t("Type")}: </span>
                      )}
                      {exam.examAlgorithm}
                    </Typography>
                  </Tooltip>
                </Grid>

                <Grid item xs={12} sm={3}>
                  <Tooltip title={exam.examSchedule}>
                    <Typography
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      maxWidth="fit-content"
                      sx={{
                        fontSize: "14px",
                        paddingRight: "16px",
                      }}
                    >
                      {smallerThanSm && (
                        <span style={{ fontWeight: 600 }}>
                          {t("Scheduled")}:{" "}
                        </span>
                      )}
                      {exam.examSchedule}
                    </Typography>
                  </Tooltip>
                </Grid>
              </Grid>

              <Grid
                item
                xs={1}
                sx={{
                  display: "flex",
                  height: "100%",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: { xs: "12px", sm: "0px" },
                }}
              >
                <ExamCardButtons
                  exam={examToUse}
                  selectedPatient={selectedPatient}
                  refetchExams={refetchExams}
                />
              </Grid>
            </Grid>
          </Grid>
        </TableCard>
      </Grid>
    </Grid>
  );
};

export default ExamCard;
